import { FunctionComponent, useEffect, useState } from 'react';
import {
  AvailabilityIndicator,
  BodyText,
  Button,
  CollapsibleBlock,
  CollapsibleBlockList,
  Div,
  Flex,
  Gutter,
  Icon,
  ModalDialog,
  Tag,
  Title,
} from '@gaiads/telia-react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  AssetLeaseTimePeriod,
  COUNTRIES,
  DataTable,
  DATE_FORMAT,
  createTicket,
  EolFlowFinishFromChoice,
  EndOfLeaseChoice,
  EndOfLeaseChoiceTranslations,
  EndOfLeaseNotification,
  EndOfLeaseNotificationTranslations,
  getDaysToTheLeaseEndDecision,
  getLeaseEndDate,
  getLeaseEndDateActual,
  getLeaseMonthsLeft,
  getLeaseStartDate,
  getReturnStatusMessage,
  ReturnStatusTranslations,
  RowItem,
  shouldShowEndOfLeaseNotification,
} from '@cdaas/reusable-components-daas';
import {
  AssetEndOfLeaseReason,
  AssetEndOfLeaseRequest,
  AssetState,
  Currency,
  MyAssetResponse,
  UserPoliciesResponseSingleDevicePolicyEnum,
} from '@cdaas/api-clients/dist/asset-user-openapi';

import { UserAssetResponseWithAutoOpen } from '../../types';
import BuyoutPriceNotAvailableYetNotification from './Buyout/BuyoutPriceNotAvailableYetNotification';
import {
  confirmOrCancelEndOfLeaseDecision,
  getMyAsset as getMyAssetApi,
  getTicketURI,
  isWebshopEnabled,
  postEndOfLeaseDecision,
  renderContactSupportText,
} from '../../utils';
import { RootState } from '../../store/reducers';

import './AssetCard.css';

import { AssetCardProps } from './types';
import { refreshUserInfo, showGrowlNotification } from '../../store/actions';
import { useHistory } from 'react-router-dom';

interface UnsortedTimelineRow {
  date: DateTime;
  text: string;
}

const AssetCard: FunctionComponent<AssetCardProps> = ({ asset, collapsibleBlocksExpanded = false }) => {
  const history = useHistory();
  const [isCardOpen, setIsCardOpen] = useState<boolean>(false);
  const [myAsset, setMyAsset] = useState<MyAssetResponse>();
  const [secondTicketModalOpen, setSecondTicketModalOpen] = useState(false);
  const authenticatedUser = useSelector((state: RootState) => state.authentication.user);
  const organization = useSelector((state: RootState) => state.authentication.user?.organization);
  const dispatch = useDispatch();
  const assetId = asset.asset.id;

  const useBuyoutWithPriceFlow = useSelector((state: RootState) => state.featureFlags.FEATURE_FLAG_FRONTEND_FLOW_BUYOUT_WITH_PRICE_ENABLED);
  const recurringMonthlyExtensionNoEnabled = useSelector(
    (state: RootState) => state.featureFlags.FEATURE_FLAG_RECURRING_MONTHLY_EXTENSION_NO_ENABLED,
  );

  const getLatestUnconfirmedEolBuyoutDecision = (asset: MyAssetResponse | undefined) => {
    return (asset?.unconfirmed_end_of_lease_decisions || []).find(unconfirmedEolDecision => !!unconfirmedEolDecision.buyout);
  };
  const latestUnconfirmedEolBuyoutDecision = getLatestUnconfirmedEolBuyoutDecision(myAsset);

  const { t } = useTranslation('assets');

  const { t: tGlobal } = useTranslation('globalComponents');
  const { t: tShop } = useTranslation('shop');

  const automaticExtension: boolean = myAsset?.prices?.lease?.can_lease_be_auto_extended ?? false;

  const contractEndsTitleMessage = t(
    recurringMonthlyExtensionNoEnabled && automaticExtension
      ? 'AssetCard.endOfLeaseNotifications.contractEndsTitleUpgrade'
      : 'AssetCard.endOfLeaseNotifications.contractEndsTitle',
  );

  const decisionNotYetDoneMessage = t(
    automaticExtension
      ? recurringMonthlyExtensionNoEnabled
        ? 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionRecurring'
        : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtension'
      : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageLeaseEnd',
  );

  const daysLeftForDecisionMessage = t(
    recurringMonthlyExtensionNoEnabled
      ? automaticExtension
        ? 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionRecurring'
        : 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionLast'
      : 'AssetCard.endOfLeaseNotifications.daysLeftForDecision',
  );

  const endOfLeaseChoiceTranslations: EndOfLeaseChoiceTranslations = {
    buyoutDeviceOptionTitle: t('AssetCard.assetActions.purchaseDeviceOption'),
    buyoutDeviceOptionText: t('AssetCard.assetActions.purchaseDeviceText'),
    buyoutDeviceOptionWithoutPriceButton: t('AssetCard.assetActions.purchaseDeviceButton'),
    buyoutDeviceOptionWithPricePurchaseButton: t('AssetCard.assetActions.purchaseButton'),
    buyoutDeviceOptionWithPriceAskForPriceButton: t('AssetCard.assetActions.askForPriceButton'),
    buyoutDeviceOptionWithPricePurchaseAnywayButton: t('AssetCard.assetActions.purchaseAnywayButton'),
    buyoutDeviceOptionWithPriceName: t('AssetCard.assetActions.purchaseDevicePriceName'),
    buyoutDeviceOptionWithPriceIncludingVat: t('AssetCard.assetActions.purchaseDevicePriceIncludingVat'),
    buyoutDeviceOptionWithPriceExcludingVat: t('AssetCard.assetActions.purchaseDevicePriceExcludingVat'),
    buyoutDeviceOptionWithPriceWaitingForPrice: t('AssetCard.assetActions.waitingForPrice'),
    buyoutDeviceWithPriceWaitingPriceNotificationTitle: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceTitle'),
    buyoutDeviceWithPriceWaitingPriceNotificationText: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceMessage'),
    buyoutDeviceWithPriceEolNotificationContractEndsTitle: contractEndsTitleMessage,
    buyoutDeviceWithPriceEolNotificationDecisionNotYetDoneMessage: decisionNotYetDoneMessage,
    buyoutDeviceWithPriceEolNotificationDaysLeftForDecision: daysLeftForDecisionMessage,
    buyoutDeviceWithPriceEolNotificationBuyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationBuyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessageAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnTitle: t('AssetCard.endOfLeaseNotifications.returnTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnMessage: t('AssetCard.endOfLeaseNotifications.returnMessageAdmin'),
    buyoutDeviceModalPurchaseAnywayTitle: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.title'),
    buyoutDeviceModalPurchaseAnywayText1: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph1'),
    buyoutDeviceModalPurchaseAnywayText2: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph2'),
    buyoutDeviceModalPurchaseAnywayText3: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph3'),
    buyoutDeviceModalPurchaseAnywayConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalPurchaseAnywayCancelButton: t('AssetCard.cancelButton'),
    buyoutDevicePurchaseConfirmationCheckboxLabel: t('AssetCard.purchaseConfirmationCheckboxLabel'),
    buyoutDeviceModalNoPriceAvailableTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.title'),
    buyoutDeviceModalNoPriceAvailableDescription: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.description'),
    buyoutDeviceModalNoPriceAvailableOptionsTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionsTitle'),
    buyoutDeviceModalNoPriceAvailableOptionWait: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionWait'),
    buyoutDeviceModalNoPriceAvailableOptionPurchaseAnyway: t(
      'AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionPurchaseAnyway',
    ),
    buyoutDeviceModalNoPriceAvailableOptionCancel: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionCancel'),
    buyoutDeviceModalNoPriceAvailableCancelButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.cancelButton'),
    buyoutDeviceModalNoPriceAvailableWaitButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.waitButton'),
    buyoutDeviceModalNoPriceAvailablePurchaseButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.purchaseButton'),
    buyoutDeviceModalPriceAvailableTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.title'),
    buyoutDeviceModalPriceAvailableText1: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph1'),
    buyoutDeviceModalPriceAvailableText2: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph2'),
    buyoutDeviceModalPriceAvailableCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalPriceAvailableConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalWithoutPriceTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.title'),
    buyoutDeviceModalWithoutPriceText1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part1'),
    buyoutDeviceModalWithoutPriceText2_1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_1'),
    buyoutDeviceModalWithoutPriceText2_2: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_2'),
    buyoutDeviceModalWithoutPriceConfirmButton: t('AssetCard.purchaseConfirmButton'),
    buyoutDeviceModalWithoutPriceCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalWithoutPriceConfirmedTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedTitle'),
    buyoutDeviceModalWithoutPriceConfirmedText: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedText'),
    buyoutDeviceModalWithoutPriceConfirmedButton: t('AssetCard.okButton'),
    upgradeDeviceOptionText: t('AssetCard.assetActions.upgradeDeviceText'),
    upgradeDeviceModalText1: t('AssetCard.upgradeDeviceModal.text1'),
    upgradeDeviceModalText2: t('AssetCard.upgradeDeviceModal.text2'),
    upgradeDeviceModalText3: t('AssetCard.upgradeDeviceModal.text3'),
    extendDeviceOptionTitle: t('AssetCard.assetActions.extendDeviceOption'),
    extendDeviceOptionTextStart: t('AssetCard.assetActions.extendDeviceTextReusableStart'),
    extendDeviceOptionTextEnd: t('AssetCard.assetActions.extendDeviceTextReusableEnd'),
    extendDeviceOptionButton: t('AssetCard.assetActions.extendDeviceButton'),
    extendDeviceModalsTitle: t('AssetCard.extendDeviceModals.title'),
    extendDeviceModalsText: t('AssetCard.extendDeviceModals.text'),
    extendDeviceModalsOptionTitle: t('AssetCard.extendDeviceModals.optionTitle'),
    extendDeviceModalsMonths: t('AssetCard.months'),
    extendDeviceModalsConfirmSelectionTitle: t('AssetCard.extendDeviceModals.confirmSelectionTitle'),
    extendDeviceModalsConfirmSelectionText: t('AssetCard.extendDeviceModals.confirmSelectionText'),
    extendDeviceModalsConfirmedTitle: t('AssetCard.extendDeviceModals.confirmedTitle'),
    extendDeviceModalsNextButton: t('AssetCard.nextButton'),
    extendDeviceModalsConfirmButton: t('AssetCard.confirmButton'),
    extendDeviceModalsCancelButton: t('AssetCard.cancelButton'),
    extendDeviceModalsOkButton: t('AssetCard.okButton'),
    extendDeviceOrSeparator: t('AssetCard.extendDeviceModals.orSeparator'),
    returnDeviceOptionTitle: t('AssetCard.assetActions.returnDeviceOption'),
    returnDeviceOptionText: t('AssetCard.assetActions.returnDeviceText'),
    returnDeviceOptionButton: t('AssetCard.assetActions.returnDeviceButton'),
    returnDeviceModalTitle: t('AssetCard.returnDeviceModal.title'),
    returnDeviceModalContinueButton: t('AssetCard.continueButton'),
    returnDeviceModalCancelButton: t('AssetCard.cancelButton'),
    returnDeviceModalText1: t('AssetCard.returnDeviceModal.text1'),
    returnDeviceModalText2: t('AssetCard.returnDeviceModal.text2'),
    returnDeviceModal: {
      nextSteps: t('AssetCard.returnDeviceModal.nextSteps'),
      step1: t('AssetCard.returnDeviceModal.step1'),
      step2: t('AssetCard.returnDeviceModal.step2'),
      step2Detail: t('AssetCard.returnDeviceModal.step2Detail'),
      step3: t('AssetCard.returnDeviceModal.step3'),
      step3Detail: t('AssetCard.returnDeviceModal.step3Detail'),
      step4: t('AssetCard.returnDeviceModal.step4'),
      step4Detail: t('AssetCard.returnDeviceModal.step4Detail'),
    },
    returnDeviceReturnConfirmedHeadline: t('ReturnAsset.returnConfirmed.headline'),
    returnDeviceReturnConfirmedUpgradeTitle: t('ReturnAsset.returnConfirmed.upgradeTitle'),
    returnDeviceReturnConfirmedText1: t('ReturnAsset.returnConfirmed.text1'),
    returnDeviceReturnConfirmedText2: t('ReturnAsset.returnConfirmed.text2'),
    returnDeviceReturnConfirmedText3: t('ReturnAsset.returnConfirmed.text3'),
    returnDeviceReturnConfirmedNextButton: t('ReturnAsset.returnConfirmed.nextButton'),
    returnDeviceReturnConfirmedUpgradeButton: t('ReturnAsset.returnConfirmed.upgradeNextButton'),
    returnDeviceOrderReturn: {
      headline: t('ReturnAsset.orderReturnEnvelope.headline'),
      checkoutDetails: {
        employeeTitle: tShop('checkout.detailsPage.employeeTitle'),
        deliveryTitle: t('ReturnAsset.orderReturnEnvelope.deliveryTitle'),
        deliveryText: t('ReturnAsset.orderReturnEnvelope.deliveryText'),
        recipientText: t('ReturnAsset.orderReturnEnvelope.recipientText'),
        contactText: t('ReturnAsset.orderReturnEnvelope.contactText'),
        deliveryQuestion: t('ReturnAsset.orderReturnEnvelope.deliveryQuestion'),
        companyName: t('ReturnAsset.orderReturnEnvelope.companyName'),
        firstName: tShop('checkout.detailsPage.form.firstName'),
        lastName: tShop('checkout.detailsPage.form.lastName'),
        email: tShop('checkout.detailsPage.form.email'),
        phoneNbr: tShop('checkout.detailsPage.form.phoneNbr'),
        streetAddress: t('ReturnAsset.orderReturnEnvelope.streetAddress'),
        city: tShop('checkout.detailsPage.form.city'),
        postalCode: tShop('checkout.detailsPage.form.postalCode'),
        country: tShop('checkout.detailsPage.form.country'),
        countryPlaceholder: tShop('checkout.detailsPage.form.countryPlaceholder'),
        homeDeliveryOption: tShop('checkout.detailsPage.form.homeDeliveryOption'),
        homeDeliveryOptionDetails: t('ReturnAsset.orderReturnEnvelope.homeDeliveryOptionDetails'),
        companyDeliveryOption: tShop('checkout.detailsPage.form.companyDeliveryOption'),
        companyDeliveryOptionDetails: t('ReturnAsset.orderReturnEnvelope.companyDeliveryOptionDetails'),
        required: tShop('checkout.detailsPage.form.rules.required'),
        invalidEmail: tShop('checkout.detailsPage.form.rules.invalidEmail'),
        invalidPhoneNumber: tShop('checkout.detailsPage.form.rules.invalidPhoneNumber'),
        invalidPostalCode: tShop('checkout.detailsPage.form.rules.invalidPostalCode'),
        returnConfirmationCheckboxLabel: t('ReturnAsset.orderReturnEnvelope.returnConfirmationCheckboxLabel'),
        nextButton: t('ReturnAsset.orderReturnEnvelope.confirmButton'),
        cancelButton: t('ReturnAsset.orderReturnEnvelope.cancelButton'),
        Finland: tGlobal('countries.Finland'),
        Sweden: tGlobal('countries.Sweden'),
        Norway: tGlobal('countries.Norway'),
        Denmark: tGlobal('countries.Denmark'),
        UnitedKingdom: tGlobal('countries.UnitedKingdom'),
      },
      returnableDevice: {
        title: t('ReturnAsset.returnReturnableDevice.title'),
        text1: t('ReturnAsset.returnReturnableDevice.text1'),
        text2: t('ReturnAsset.returnReturnableDevice.text2'),
      },
    },
  };

  const endOfLeaseNotificationTranslations: EndOfLeaseNotificationTranslations = {
    contractEndsTitle: contractEndsTitleMessage,
    decisionNotYetDoneMessage: decisionNotYetDoneMessage,
    daysLeftForDecision: daysLeftForDecisionMessage,
    buyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitle'),
    buyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessage'),
    returnTitle: t('AssetCard.endOfLeaseNotifications.returnTitle'),
    returnMessage: t('AssetCard.endOfLeaseNotifications.returnMessage'),
  };

  const returnStatusTranslations: ReturnStatusTranslations = {
    deviceAwaitingReturn: t('AssetCard.returnStatus.deviceAwaitingReturn'),
    deviceReturnInTransit: t('AssetCard.returnStatus.deviceReturnInTransit'),
    deviceReturnReceived: t('AssetCard.returnStatus.deviceReturnReceived'),
    deviceReturnedApproved: t('AssetCard.returnStatus.deviceReturnedApproved'),
    deviceReturnedWithCloudLock: t('AssetCard.returnStatus.deviceReturnedWithCloudLock'),
    deviceReturnedWithConditions: t('AssetCard.returnStatus.deviceReturnedWithConditions'),
    incorrectDeviceReturned: t('AssetCard.returnStatus.incorrectDeviceReturned'),
    deviceReturned: t('AssetCard.returnStatus.deviceReturned'),
    deviceNotReturnedInvoiced: t('AssetCard.returnStatus.deviceNotReturnedInvoiced'),
    otherReturnStatus: t('AssetCard.returnStatus.otherReturnStatus'),
  };

  useEffect(() => {
    if (asset?.autoOpen) {
      setIsCardOpen(true);
    }
  }, [asset?.asset, asset?.autoOpen]);

  const getMyAsset = async (assetId: string): Promise<void> => {
    const asset = await getMyAssetApi(assetId);
    if (asset) setMyAsset(asset);
  };

  useEffect(() => {
    if (!myAsset) {
      getMyAsset(asset?.asset.id ?? '');
    }
    // myAsset cannot be dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.asset.id]);

  if (!asset) {
    return (
      <Div alignTextToCenter>
        <Title>{t('AssetCard.canNotShowDeviceError')}</Title>
      </Div>
    );
  }

  const endDate = getLeaseEndDateActual(asset.asset.prices.lease) ?? getLeaseEndDate(asset.asset.prices.lease);
  const startDate = getLeaseStartDate(asset.asset.prices.lease);
  const monthsLeft = Number(getLeaseMonthsLeft(startDate, endDate).toFixed(0)) || 0;
  const daysLeft = endDate.isValid ? getDaysToTheLeaseEndDecision(asset.asset.prices.lease?.end_date_planned || '') : '?';

  const createDeviceDetailsRows = (asset: UserAssetResponseWithAutoOpen): RowItem[] => {
    const rows: RowItem[] = [
      {
        rowType: 'md',
        labelItem: { label: t('AssetCard.imeiNumber') },
        dataItem: { data: asset.asset.serial_number.number },
      },
    ];

    if (asset?.asset.serial_number_history && asset.asset.serial_number_history?.length > 1) {
      rows.push({
        rowType: 'md',
        labelItem: { label: t('AssetCard.previousImeis') },
        dataItem: {
          data: (
            <BodyText strikeThrough>
              {asset.asset.serial_number_history
                ?.filter(serNo => serNo.number !== asset.asset.serial_number.number && serNo.type === 'imei')
                .map(serNo => serNo.number)
                .join(', ')}
            </BodyText>
          ),
        },
      });
    }

    rows.push(
      {
        rowType: 'md',
        labelItem: { label: t('AssetCard.sku') },
        dataItem: { data: asset.asset.product.sku },
      },
      {
        rowType: 'md',
        labelItem: { label: t('AssetCard.bundleAccessories') },
        dataItem: {
          data: (
            <>
              {asset.asset.subassets
                .filter(subAsset => subAsset.product.type === 'hardware')
                .map(subAsset => (
                  <BodyText key={subAsset.id}>{subAsset.product.name}</BodyText>
                ))}
            </>
          ),
        },
      },
      {
        rowType: 'md',
        labelItem: { label: t('AssetCard.bundleServices') },
        dataItem: {
          data: (
            <>
              {asset.asset.insured && (
                <BodyText
                  key={'insurance'}
                  color="purple500"
                  onClick={() => globalThis.open(process.env.REACT_APP_INSURANCE_URI || '', '_blank')}
                  clickableAppearance={true}
                >
                  {t('AssetCard.insurance')}
                </BodyText>
              )}
              {asset.asset.subassets
                .filter(subAsset => subAsset.product.type === 'service')
                .map(subAsset => (
                  <BodyText key={subAsset.id}>{subAsset.product.name}</BodyText>
                ))}
            </>
          ),
        },
      },
    );

    return rows;
  };

  const handleCreateTicket = () => {
    const url = getTicketURI(organization?.locale.language);
    createTicket(url, myAsset);
  };

  const createTicketRows = (asset: UserAssetResponseWithAutoOpen): RowItem[] => {
    const openTickets = asset.asset?.tickets.filter(ticket => ticket.open) || [];
    const closedTickets = asset.asset?.tickets.filter(ticket => !ticket.open) || [];

    return [...openTickets, ...closedTickets].map(ticket => ({
      rowType: 'md',
      labelItem: { label: `${t('AssetCard.ticketStatus')}: ${ticket.open ? t('AssetCard.ticketOpen') : t('AssetCard.ticketClosed')}` },
      dataItem: {
        data: (
          <Button
            variant="text"
            iconName="external"
            iconOnRight
            size="xl"
            textVariantProps={{
              color: 'purple500',
            }}
            onClick={() => globalThis.open(ticket.url)}
          >
            {t('AssetCard.link')}
          </Button>
        ),
      },
    }));
  };

  const renderAssetTimeline = (asset: UserAssetResponseWithAutoOpen, t: TFunction<'assets'>) => {
    // if more than one history entry, then remove the oldest lease period
    const tempHistory = [...(asset.asset.prices.lease?.history ?? [])];
    tempHistory.pop();

    const unsortedRows: UnsortedTimelineRow[] = [];

    if (asset.asset.prices.lease?.end_date_actual) {
      unsortedRows.push({
        date: DateTime.fromFormat(asset.asset.prices.lease?.end_date_actual, 'yyyy-MM-dd'),
        text: t('AssetCard.contractEnded'),
      });
    }

    if (asset.asset.prices.lease?.return_package?.shipped_at) {
      unsortedRows.push({
        date: DateTime.fromISO(asset.asset.prices.lease?.return_package?.shipped_at),
        text: t('AssetCard.returnEnvelopeSent'),
      });
    }

    if (asset.asset.prices.lease?.end_of_lease_reason) {
      let endOfLeaseEvent: string = '';
      switch (asset.asset.prices.lease?.end_of_lease_reason) {
        case AssetEndOfLeaseReason.Return:
          endOfLeaseEvent = t('AssetCard.eolActionEventMessage.returnDeviceEvent');
          break;
        case AssetEndOfLeaseReason.Buyout:
          endOfLeaseEvent = t('AssetCard.eolActionEventMessage.buyoutSelectedEvent');
          break;
        case AssetEndOfLeaseReason.NotReturned:
          endOfLeaseEvent = t('AssetCard.eolActionEventMessage.notReturnedEvent');
          break;
      }
      unsortedRows.push({
        date: DateTime.fromISO(asset.asset.prices.lease?.end_of_lease_selected ?? ''),
        text: endOfLeaseEvent,
      });
    }

    if (tempHistory.length) {
      for (const entry of tempHistory) {
        const monthsExtended = entry.lease_period_months;
        unsortedRows.push({
          date: DateTime.fromFormat(entry.start_date, 'yyyy-MM-dd'),
          text: `${t('AssetCard.contractExtended')} ${monthsExtended} ${t('AssetCard.monthShort', { count: monthsExtended })}`,
        });
      }
    }

    if (asset.asset.delivery_date) {
      unsortedRows.push({
        date: DateTime.fromISO(asset.asset.delivery_date ?? ''),
        text: t('AssetCard.delivered'),
      });
    }

    if (asset.asset.shipment_date) {
      unsortedRows.push({
        date: DateTime.fromISO(asset.asset.shipment_date ?? ''),
        text: t('AssetCard.shipped'),
      });
    }

    // Sort rows by date. First, reset DateTime objects to midnight,
    // so that values with the same date are considered equal.
    // Then, because Array.sort() implements a stable sort,
    // the order of rows with the same date is preserved.
    // This is reverse sort, so comparing with `b - a` instead of `a - b`.
    const timelineRows: RowItem[] = unsortedRows
      .map(row => ({
        date: row.date.startOf('day'),
        text: row.text,
      }))
      .sort((a, b) => b.date.toMillis() - a.date.toMillis())
      .map(row => ({
        rowType: 'md',
        labelItem: { label: row.date.toFormat(DATE_FORMAT) },
        dataItem: { data: row.text },
      }));

    return timelineRows;
  };

  const assetStatus = (): string => {
    let eolStatus = '';
    const contractEndDate = endDate.isValid ? endDate.toFormat(DATE_FORMAT) : '';
    if (asset.asset.state === AssetState.Inactive) {
      const contractEndedDate = endDate.isValid && endDate < DateTime.now() ? `${t('AssetCard.contractEndedDate')} ${contractEndDate}, ` : '';
      // TODO - should differentiate between 1. user has made decision for buyout and 2. user has actually paid
      // Backend does not yet provide info if user has actually paid
      switch (asset.asset.prices.lease?.end_of_lease_reason) {
        case AssetEndOfLeaseReason.Buyout:
          eolStatus = t('AssetCard.eolMadeDecision.deviceBought');
          break;
        case AssetEndOfLeaseReason.Return:
          eolStatus = getReturnStatusMessage(asset.asset.prices.lease?.return_status, returnStatusTranslations);
          break;
        case AssetEndOfLeaseReason.NotReturned:
          eolStatus = t('AssetCard.eolMadeDecision.deviceNotReturned');
          break;
      }
      return `${contractEndedDate}${eolStatus}`;
    } else {
      if (asset.asset.prices.lease?.end_of_lease_reason === AssetEndOfLeaseReason.Return) {
        eolStatus = `, ${getReturnStatusMessage(asset.asset.prices.lease?.return_status, returnStatusTranslations)}`;
      }
      return `${t('AssetCard.validUntil')} ${contractEndDate}${eolStatus}`;
    }
  };

  const handleCreateDraftDecisionRequest = async () => {
    const res = await postEndOfLeaseDecision(assetId, 'user', 'buyout', undefined, undefined, false);
    if (!res?.unconfirmed_end_of_lease_decisions?.length) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionAskForBuyoutPricePostError'), 'error'));
      return undefined;
    }
    return res.unconfirmed_end_of_lease_decisions;
  };

  const handleConfirmDecisionRequest = async (request: AssetEndOfLeaseRequest) => {
    // Handle eol return decision
    if (request.return) {
      const assetResponse = await postEndOfLeaseDecision(assetId, 'user', 'return', request.return.from, undefined, request.confirm);
      if (assetResponse) {
        return true;
      }
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
      return false;
    } else {
      // Handle other than return eol decision
      const assetResponse = await postEndOfLeaseDecision(
        assetId,
        'user',
        request.buyout ? 'buyout' : 'extension',
        undefined,
        request.extension ? request.extension.length : undefined,
        request.confirm,
      );
      if (assetResponse) {
        // Refresh user's single device policy info, to allow them to order
        // a new device right away if they are now allowed to do so.
        await dispatch(refreshUserInfo());
        if (request.extension) {
          dispatch(
            showGrowlNotification(t('AssetCard.eolMadeDecision.successfulExtensionNotice', { numberOfMoths: request.extension.length }), 'success'),
          );
          // Show new extended lease time right away
          setMyAsset(assetResponse);
        }
        return true;
      } else {
        dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
        return false;
      }
    }
  };

  const handleConfirmOrCancelDraftDecisionRequest = async (eolId: string, confirm: boolean) => {
    const res = await confirmOrCancelEndOfLeaseDecision(assetId, eolId, confirm, 'user');
    if (!res && !confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionBuyoutCancelError'), 'error'));
    } else if (!res && confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
    } else if (res && !confirm) {
      setMyAsset(res as MyAssetResponse);
      dispatch(showGrowlNotification(t('AssetCard.endOfLeaseNotifications.priceRequestCancelled'), 'warning'));
    }
    return !!res;
  };

  const handleEndOfLeaseFlowFinish = (selectedChoice: EolFlowFinishFromChoice) => {
    dispatch(showGrowlNotification(t('AssetCard.eolDecisionMade'), 'success'));
    switch (selectedChoice) {
      case 'upgrade':
        dispatch(refreshUserInfo()).then(() => history.push(isWebshopEnabled(null, organization) ? '/webshop' : '/shop'));
        break;
      case 'return':
        dispatch(refreshUserInfo()).then(() => getMyAsset(assetId));
        break;
      case 'buyout':
        dispatch(refreshUserInfo()).then(() => getMyAsset(assetId));
        break;
      case 'extension':
      default:
        void getMyAsset(assetId);
        break;
    }
  };

  const leasePeriodMonths = asset.asset.prices.lease?.lease_period_months;

  const singleDevicePolicyInEffect = (): boolean =>
    authenticatedUser?.policies?.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect;

  /**
   * Device limit notification should be shown only if the end-of-lease decision should be made for active device and the user has a single
   * device policy in effect.
   */
  const showDeviceLimitNotification = (): boolean => Boolean(myAsset?.end_of_lease_options) && singleDevicePolicyInEffect();

  const closeSecondTicketModal = () => setSecondTicketModalOpen(false);

  return (
    <Div boxShadow={'lg'} borderRadius={{ size: 'lg' }} padding={{ size: 'lg' }} backgroundColor="white">
      {/* Main asset card */}
      <Flex spaceBetween clickableAppearance hoverColor={'purple500'} onClick={() => setIsCardOpen(prev => !prev)} pileVerticallyTo={'xxs'}>
        <Flex directionColumn flexItem>
          <Title size={'sm'}>{asset.asset.product.name}</Title>
          <AvailabilityIndicator color={asset.asset.state === AssetState.Active ? 'green700' : 'gray300'} margin={{ top: 'xs' }}>
            <BodyText color={'gray500'} size={'sm'}>
              {assetStatus()}
            </BodyText>
          </AvailabilityIndicator>

          <Gutter size="xs" />

          <Flex inlineBlock>
            {!!asset.asset.location_reference && (
              <Tag margin={{ left: 'xs' }} backgroundColor="purple500" className="tag-with-ellipsis">
                {asset.asset.location_reference}
              </Tag>
            )}

            {asset.asset?.serial_number_history && asset.asset.serial_number_history.length > 1 && (
              <Tag margin={{ left: 'xs' }} backgroundColor="purple500" className="tag-with-ellipsis">
                {t('AssetCard.replacedDeviceTag')}
              </Tag>
            )}
          </Flex>
        </Flex>

        <Flex directionColumn alignRight alignTextToRight flexItem>
          <Title size={'sm'}>
            {asset.asset.prices.lease?.price.total ?? ''} {organization?.locale.currency.toUpperCase() ?? ''}/
            {`${t('AssetCard.monthShort', { count: 1 })} ${t('AssetCard.vatIncluded')}`}
          </Title>
          <Gutter size={'xs'} />
          <BodyText color={'gray500'} size={'sm'}>
            {asset.asset.state === AssetState.Active && `${leasePeriodMonths} ${t('AssetCard.monthShort', { count: leasePeriodMonths })}`}
          </BodyText>
        </Flex>

        <Flex directionColumn alignTextToRight margin={{ left: 'md' }}>
          <Icon name={isCardOpen ? 'chevron-up' : 'chevron-down'} size={'md'} clickableAppearance />
        </Flex>
      </Flex>

      {/* CARD OPENED */}
      {isCardOpen && (
        <>
          {/* Show Time Period only if asset lease is active */}
          {!asset.asset.prices.lease?.end_of_lease_reason && (
            <AssetLeaseTimePeriod
              lease={asset.asset.prices.lease}
              translations={{
                monthLeft: t('AssetCard.monthLeft', { count: monthsLeft }),
                monthsLeft: t('AssetCard.monthsLeft', { count: monthsLeft }),
              }}
            />
          )}

          <Gutter />

          <CollapsibleBlockList>
            <CollapsibleBlock blockTitle={t('AssetCard.deviceDetails')} expanded={collapsibleBlocksExpanded}>
              <CollapsibleBlock.Content>
                <DataTable tableStyle="regular" rows={createDeviceDetailsRows(asset)} />
              </CollapsibleBlock.Content>
            </CollapsibleBlock>

            <CollapsibleBlock blockTitle={t('AssetCard.events')} expanded={collapsibleBlocksExpanded}>
              <CollapsibleBlock.Content>
                <DataTable tableStyle="regular" rows={renderAssetTimeline(asset, t)} />
              </CollapsibleBlock.Content>
            </CollapsibleBlock>
            <CollapsibleBlock blockTitle={t('AssetCard.tickets')} expanded={collapsibleBlocksExpanded}>
              <CollapsibleBlock.Content>
                <DataTable tableStyle="regular" rows={createTicketRows(asset)} />
              </CollapsibleBlock.Content>
            </CollapsibleBlock>
          </CollapsibleBlockList>

          <Gutter size={'lg'} />
        </>
      )}

      {/* Show EOL notification if EOL options are available or asset is active and EOL decision is made */}
      {myAsset?.state === AssetState.Active && shouldShowEndOfLeaseNotification(myAsset) && (
        <>
          {!isCardOpen && <Gutter />}
          <EndOfLeaseNotification
            daysLeftForDecision={daysLeft}
            endOfLeaseReason={myAsset?.prices?.lease?.end_of_lease_reason || undefined}
            translations={endOfLeaseNotificationTranslations}
          />
          {isCardOpen && <Gutter />}
        </>
      )}

      {isCardOpen && (
        <>
          {showDeviceLimitNotification() && (
            <BodyText color="gray800" size="sm" margin={{ bottom: 'md' }}>
              <Trans ns="assets" i18nKey={'AssetCard.deviceLimitNotification'} />
            </BodyText>
          )}
          {/* Action cards */}
          {myAsset?.end_of_lease_options?.return && (
            <EndOfLeaseChoice
              companyName={organization?.name || ''}
              handleConfirmDecisionRequest={handleConfirmDecisionRequest}
              handleConfirmOrCancelDraftDecisionRequest={handleConfirmOrCancelDraftDecisionRequest}
              handleCreateDraftDecisionRequest={handleCreateDraftDecisionRequest}
              handleEndOfLeaseFlowFinish={handleEndOfLeaseFlowFinish}
              countries={[COUNTRIES.find(country => country.value === 'NO')!]}
              countryCallingCodes={COUNTRIES}
              currency={organization?.locale.currency || ('' as Currency)}
              asset={myAsset}
              buyoutWithPriceFlow={useBuyoutWithPriceFlow}
              showUpgrade={true}
              showNotificationWithDaysLeftOrMadeDecision={false}
              selectedUser={authenticatedUser ?? undefined}
              showSelectedUser={false}
              translations={endOfLeaseChoiceTranslations}
            />
          )}
        </>
      )}

      {/* This condition should only ever be true when new buyout with price -flow is used.
      If old flow is used, there should be no unconfirmed EOL buyout decision. */}
      {!isCardOpen && latestUnconfirmedEolBuyoutDecision?.buyout && !latestUnconfirmedEolBuyoutDecision?.buyout.price && (
        <>
          <Gutter />
          <BuyoutPriceNotAvailableYetNotification />
          <Gutter />
        </>
      )}
      {isCardOpen && myAsset?.state === AssetState.Active && (
        <>
          <Button variant="secondary" iconName="external" iconOnRight onClick={handleCreateTicket} margin={{ right: 'sm', top: 'sm' }}>
            {t('AssetCard.createTicketButton')}
          </Button>
          <Button variant="secondary" onClick={() => setSecondTicketModalOpen(true)} margin={{ top: 'sm' }}>
            {t('AssetCard.createSecondTicketButton')}
          </Button>
        </>
      )}
      <ModalDialog
        isOpen={secondTicketModalOpen}
        acceptButtonText={t('AssetCard.secondTicketModal.button')}
        title={t('AssetCard.secondTicketModal.title')}
        onAcceptClick={closeSecondTicketModal}
        onCloseClick={closeSecondTicketModal}
        data-testid="insurance-modal"
      >
        {renderContactSupportText(t('AssetCard.secondTicketModal.content'))}
      </ModalDialog>
    </Div>
  );
};

export default AssetCard;
